
import { motion } from "framer-motion";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { Label } from "@/components/ui/label";
import { LogoAnimation } from "@/components/home/LogoAnimation";
import { Features } from "@/components/home/Features";
import { TrendingTabs } from "@/components/home/TrendingTabs";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const Home = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('launch_notifications')
        .insert([{ email }]);

      if (error) throw error;

      toast({
        title: "Success!",
        description: "You'll be notified when we launch.",
      });
      setEmail("");
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 w-full overflow-y-auto bg-black bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-[#00F0FF]/10 via-black to-black">
      <div className="absolute inset-0 bg-[url('/lovable-uploads/0825329d-e13c-4e91-b73a-dc4c57b5a4b1.png')] bg-cover bg-center opacity-30 mix-blend-screen pointer-events-none" />
      
      <div className="min-h-screen flex flex-col items-center justify-center px-4 pt-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-4xl"
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <h1 className="text-4xl sm:text-6xl md:text-7xl font-bold flex items-center gap-4">
              <motion.span 
                className="text-white"
                animate={{ 
                  x: isLaunching ? [-20, 20] : 0,
                  transition: { 
                    duration: 0.5,
                    times: [0, 1]
                  }
                }}
              >
                Flux
              </motion.span>
              
              <LogoAnimation 
                onLaunch={(launching) => setIsLaunching(launching)}
              />
              
              <motion.span 
                className="text-flux-cyan"
                animate={{ 
                  x: isLaunching ? [20, -20] : 0,
                  transition: { 
                    duration: 0.5,
                    times: [0, 1]
                  }
                }}
              >
                Void
              </motion.span>
            </h1>
          </div>
          
          <p className="text-flux-cyan text-xl mb-16 text-center">
            Where Ideas Ignite, Evolve, and Thrive.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link to="/signup">
              <Button 
                className="group w-full sm:w-auto bg-gradient-to-r from-[#00F0FF] to-[#0EA5E9] text-black hover:opacity-90 px-8 py-6 text-lg transition-all duration-300"
              >
                Join us <span className="inline-block transition-transform duration-300 group-hover:translate-x-2">→</span>
              </Button>
            </Link>
            <Link to="/signin">
              <Button 
                variant="outline"
                className="group w-full sm:w-auto border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg transition-all duration-300"
              >
                Sign In
              </Button>
            </Link>
          </div>

          <Features />
          <TrendingTabs />
        </motion.div>
      </div>
    </div>
  );
};

export default Home;

